import { Space } from 'antd';
import { motion } from 'framer-motion';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router';

import { RouteType } from '@/types/RouteType';

import { useWindowSize } from '@/hooks';
import PageTabs from '../PageTabs';
import { AuthoritiesType } from '@/types/Authority';
import AccessDenied from '../AccessDenied';
import { hasComboPermissions } from '@/utils/functions/hasComboPermissions';
import AccessUpgrade from '../AccessUpgrade';
import { hasPermissions } from '@/utils/functions/hasPermissions';

interface DefaultRouteTabsProps {
  route: RouteType;
  children?: ReactNode;
  baseKey?: string;
  basePath?: string;
  onChange?: (key: string) => void;
  extra?: ReactNode;
  permissions?: AuthoritiesType[];
  persistSearchParams?: boolean;
  resourceNotFound?: boolean;
}

function DefaultRouteTabs({
  route,
  children,
  baseKey,
  basePath,
  onChange,
  extra,
  permissions,
  persistSearchParams = true,
  resourceNotFound = false,
}: DefaultRouteTabsProps) {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const { isMobile } = useWindowSize();

  const defaultOnChange = (key: string) => {
    const path = basePath || route.path;

    if (persistSearchParams) {
      history.push(`${path}/${key}${!!location.search ? location.search : ''}`);
    } else {
      history.push(`${path}/${key}`);
    }
  };

  const historyKeys = history?.location.pathname.split('/');
  const startIndex = historyKeys.indexOf(baseKey || route.key);
  const selectedTabKey = historyKeys[startIndex + 1];

  const hasCombo = hasComboPermissions(permissions, true);

  if (resourceNotFound) {
    return <AccessDenied />;
  }

  return (
    <>
      <PageTabs
        onChange={onChange || defaultOnChange}
        defaultActiveKey={selectedTabKey}
        tabBarExtraContent={extra}
        items={route.routes.map((route) => ({
          key: route.key,
          label: <Space>{intl.formatMessage({ id: route.name })}</Space>,
        }))}
      />

      {permissions && !hasPermissions(permissions) ? (
        <>
          {extra && isMobile && (
            <div className="tab-wrapper-actions">{extra}</div>
          )}
          <div className="tab-wrapper">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {hasCombo && <AccessDenied />}

              {!hasCombo && <AccessUpgrade />}
            </motion.div>
          </div>
        </>
      ) : children ? (
        <>
          {extra && isMobile && (
            <div className="tab-wrapper-actions">{extra}</div>
          )}
          <div className="tab-wrapper">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {children}
            </motion.div>
          </div>
        </>
      ) : null}
    </>
  );
}

export default DefaultRouteTabs;
